import React, { useRef, useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { ConfirmModal } from "../../../boxes/ConfirmModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import { PERMISSIONS } from "../../../consts/api";
import { useDeleteManyLeadsMutation } from "../../../api/mutations/useDeleteManyLeadsMutation";
import { uiNotification } from "../../../services/UINotificationService";
import { ModalError } from "../../../boxes/ModalError/ModalError";
import { extractApiErrorData } from "../../../utilities/api";

export function withBulkDelete(Component) {
  return (props) => {
    const permission = usePermission(
      PERMISSIONS.patientManagement.bulkDeletePatients,
    );

    const { tCommon } = useAppTranslation.Common();

    const [selected, setSelected] = useState([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [errors, setErrors] = useState([]);

    const onSuccess = useRef(null);

    const { mutate, isLoading } = useDeleteManyLeadsMutation({
      onError: (e) => {
        const errors = extractApiErrorData(e)?.errors || [];

        if (errors.length) {
          setErrors(errors);
          setIsConfirmOpen(false);
        } else {
          uiNotification.error(tCommon("error.bulkDeleteLeads"));
        }
      },
      onSuccess: (data) => {
        const errors = data?.data?.data?.errors || [];

        if (errors.length) {
          setErrors(errors);
        } else {
          onSuccess?.current?.();

          setSelected([]);
          setErrors([]);

          uiNotification.success(tCommon("success.bulkDeleteLeads"));
        }

        setIsConfirmOpen(false);
      },
    });

    const getIsSelected = (id) => selected.includes(id);

    const toggleSelected = (id) => {
      if (selected.includes(id)) {
        setSelected(selected.filter((i) => i !== id));
      } else {
        setSelected([...selected, id]);
      }
    };

    return (
      <>
        <Component
          {...props}
          bulkDelete={{
            isPermitted: permission.loaded && permission.permitted,
            getIsSelected,
            toggleSelected,
            count: selected.length,
            request: (nextOnSuccess) => {
              setIsConfirmOpen(true);
              onSuccess.current = nextOnSuccess;
            },
            reset: () => {
              setSelected([]);
            },
          }}
        />
        {isConfirmOpen && (
          <ConfirmModal
            isOpen
            isConfirming={isLoading}
            onConfirm={() => mutate({ leadIds: selected.map(Number) })}
            onCancel={() => setIsConfirmOpen(false)}
            onClose={() => setIsConfirmOpen(false)}
          >
            {tCommon("confirm.bulkDeleteLeads", { count: selected.length })}
          </ConfirmModal>
        )}
        {errors.length > 0 && (
          <ModalError
            isOpen
            onClose={() => setErrors([])}
            onOk={() => setErrors([])}
          >
            <div className="d-flex flex-column gap-4">
              <h4 className="m-b-10">{tCommon("error.deleteSomeLeads")}:</h4>
              {errors.map((error) => (
                <div key={error.id}>
                  <b>{error.name} </b>
                  {error.error}
                </div>
              ))}
            </div>
          </ModalError>
        )}
      </>
    );
  };
}
